import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import mainStyles from "../../styles";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

export type CorpDomainDialogMode = "add" | "delete";

interface CorpDomainDialogProps {
  mode: CorpDomainDialogMode;
  open: boolean;
  // The domain to remove (delete mode). Ignored in add mode.
  domain?: string;
  onClose: () => void;
  // Performs the add/delete. Should reject on failure so the dialog can surface
  // the error and stay open.
  onSubmit: (domain: string) => Promise<void>;
}

// normaliseDomain mirrors the api-identity write path: trim, strip a leading
// "@", lower-case.
const normaliseDomain = (value: string): string =>
  value.trim().replace(/^@/, "").toLowerCase();

const isValidDomain = (value: string): boolean =>
  /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(normaliseDomain(value));

const CorpDomainDialog = ({
  mode,
  open,
  domain,
  onClose,
  onSubmit,
}: CorpDomainDialogProps) => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);

  const isDelete = mode === "delete";
  const [value, setValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset the field whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setValue(isDelete ? (domain ?? "") : "");
    }
  }, [open, isDelete, domain]);

  const canSubmit =
    !isProcessing && (isDelete ? Boolean(domain) : isValidDomain(value));

  const handleConfirm = async () => {
    const target = isDelete ? (domain ?? "") : normaliseDomain(value);
    setIsProcessing(true);
    try {
      await onSubmit(target);
      onClose();
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Don't allow accidental dismissal mid-request.
        if (isProcessing && reason === "backdropClick") return;
        onClose();
      }}
      aria-labelledby="corp-domain-dialog"
      fullWidth
      maxWidth="sm"
      data-testid="corp-domain-dialog"
    >
      <DialogTitle id="corp-domain-dialog">
        {isDelete ? "Delete corp domain" : "Add corp domain"}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Domain"
          value={isDelete ? (domain ?? "") : value}
          onChange={(event) => setValue(event.target.value)}
          disabled={isDelete || isProcessing}
          placeholder="example.com"
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ "data-testid": "corp-domain-input" }}
        />
        {isDelete ? (
          <Alert severity="warning" sx={classes.basicSpacing}>
            Removing <b>{domain}</b> stops the sign-up prompt for this domain —
            new sign-ups will be able to create their own Free Team again. This
            does not affect existing organisations or users.
          </Alert>
        ) : (
          <Alert severity="info" sx={classes.basicSpacing}>
            New sign-ups on this domain will be prompted to join their
            organisation instead of creating a new Free Team. This is an
            informational nudge and does not block account creation.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={classes.confirmDialogAction}>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          disabled={isProcessing}
          data-testid="corp-domain-dialog-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isDelete ? "error" : "primary"}
          onClick={handleConfirm}
          disabled={!canSubmit}
          data-testid="corp-domain-dialog-submit"
          startIcon={
            isProcessing ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isDelete ? "Delete domain" : "Add domain"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorpDomainDialog;
